import { message, Select } from "antd";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { postIncident, getAllStaffs } from "../../../helpers/api";
import _ from "lodash";
import { RequiredIndicator } from "../../../components";
import smallTalk from "smalltalk";
import { INCIDENT_TYPES, INCIDENT_STATUSES } from "../../../helpers/config";
import { getUser } from "../../../helpers/auth";

const { Option } = Select;

export default function NewIncidentForm({ onSuccess }) {
  const { handleSubmit, register, reset, setValue } = useForm();
  const user = getUser();

  const [staffId, setStaffId] = useState(null);
  const [showStaffSelect, setShowStaffSelect] = useState(false);

  const { data: staffs = [] } = useQuery({
    queryFn: () => getAllStaffs(),
    queryKey: ["staffs"],
    enabled: showStaffSelect,
  });

  const { mutateAsync, isLoading } = useMutation(
    (data) => postIncident(user.branchId, data),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 201) {
          message.success("Incident report created successfully!");
          reset();
          setStaffId(null);
          onSuccess();
          return;
        }

        throw data;
      },
      onError: (error, variables, context) => {
        const err = error.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err || "Error creating incident report");
        }
      },
      retry: true,
    }
  );

  const postSubmit = (data) => {
    const incidentData = {
      ...data,
      branchId: user.branchId,
      staffId: staffId || null,
    };

    smallTalk
      .confirm(
        "Create Incident Report",
        "You are about to create an incident report. Continue?",
        {
          buttons: {
            ok: "YES",
            cancel: "NO",
          },
        }
      )
      .then(() => mutateAsync(incidentData))
      .catch((ex) => message.error("no action taken"));
  };

  return (
    <form onSubmit={handleSubmit(postSubmit)} className="px-2">
      <div className="row">
        <div className="field col-md-4 col-12">
          <label className="mb-0" htmlFor="incidentType">
            Incident Type
            <RequiredIndicator />
          </label>
          <select
            id="incidentType"
            {...register("incidentType", { required: true })}
            className="input">
            <option value="">Select incident type</option>
            {INCIDENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field col-md-4 col-12">
          <label className="mb-0" htmlFor="incidentDate">
            Incident Date
            <RequiredIndicator />
          </label>
          <input
            type="date"
            id="incidentDate"
            {...register("incidentDate", { required: true })}
            className="input"
          />
        </div>

        <div className="field col-md-4 col-12">
          <label className="mb-0" htmlFor="incidentTime">
            Incident Time
            <RequiredIndicator />
          </label>
          <input
            type="time"
            id="incidentTime"
            {...register("incidentTime", { required: true })}
            className="input"
          />
        </div>

        <div className="field col-12">
          <label className="mb-0" htmlFor="description">
            Description
            <RequiredIndicator />
          </label>
          <textarea
            id="description"
            {...register("description", { required: true })}
            className="textarea"
            rows={4}
            placeholder="Describe the incident in detail"></textarea>
        </div>

        <div className="field col-12">
          <label className="mb-0" htmlFor="actionTaken">Action Taken</label>
          <textarea
            id="actionTaken"
            {...register("actionTaken")}
            className="textarea"
            rows={3}
            placeholder="Describe any actions taken in response to the incident"></textarea>
        </div>

        <div className="field col-md-6 col-12">
          <label className="mb-0" htmlFor="status">Status</label>
          <select
            id="status"
            {...register("status")}
            className="input"
            defaultValue="reported">
            {INCIDENT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field col-md-6 col-12">
          <label className="mb-0" htmlFor="staffId">Staff (Optional)</label>
          <Select
            id="staffId"
            placeholder="Select staff (optional)"
            allowClear
            showSearch
            optionFilterProp="children"
            value={staffId}
            onFocus={() => setShowStaffSelect(true)}
            onChange={(value) => {
              setStaffId(value);
              setValue("staffId", value);
            }}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            size="large"
            className="d-block">
            {staffs.map((staff) => (
              <Option
                key={staff.id}
                value={staff.id}>{`${staff.firstName} ${staff.lastName}`}</Option>
            ))}
          </Select>
        </div>
      </div>

      <div className="text-center my-4">
        <button
          type="submit"
          className={`button is-info ${isLoading && " is-loading"}`}
          disabled={isLoading}>
          <span className="bi bi-check-all me-2" />
          {isLoading ? "Creating..." : "Submit"}
        </button>
      </div>
    </form>
  );
}
